import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './App.css';
import { logEvent } from './firebase';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import About from './pages/About';
import Help from './pages/Help';
import Referral from './pages/Referral';
import Influencer from './pages/Influencer';

function HeroVideo() {
  const videoRef = React.useRef(null);
  const [isPlaying, setIsPlaying] = React.useState(false);

  const togglePlayback = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
        logEvent('demo_video_pause', {
          video_name: 'pholtr_web_demo'
        });
      } else {
        videoRef.current.play();
        setIsPlaying(true);
        logEvent('demo_video_play', {
          video_name: 'pholtr_web_demo'
        });
      }
    }
  };

  return (
    <div className="hero-animation">
      <div className="hero-video-container" onClick={togglePlayback}>
        <video
          ref={videoRef}
          loop
          muted
          playsInline
          preload="metadata"
          className="hero-video"
        >
          <source src="/pholtr_web_demo.mp4#t=0.1" type="video/mp4" />
        </video>
        {!isPlaying && (
          <div className="video-overlay">
            <button className="play-button">
              <div className="play-icon-circle">
                <span className="play-icon"></span>
              </div>
              <span className="play-text">Play Demo</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Home() {
  // Track page view
  React.useEffect(() => {
    logEvent('page_view', {
      page_title: 'Home',
      page_location: window.location.href,
      page_path: '/'
    });
  }, []);

  // Track App Store button click
  const handleAppStoreClick = () => {
    logEvent('click_app_store', {
      button_location: 'hero_section',
      link_url: 'https://apps.apple.com/app/pholtr/id6753979622'
    });
  };

  // Track Google Play button click
  const handlePlayStoreClick = () => {
    logEvent('click_play_store', {
      button_location: 'hero_section',
      link_url: 'https://play.google.com/store/apps/details?id=nu.hibou.prodshot'
    });
  };

  return (
    <div className="App">
      <Helmet>
        <title>Pholtr: Product Photo Editor - AI Photo Editor for eBay, Etsy & Poshmark</title>
        <meta name="description" content="Turn clutter into cash with Pholtr. Professional AI product photography for online sellers. Remove backgrounds, fix lighting, and create studio-quality photos for eBay, Poshmark, Etsy, Depop, Mercari & Shopify in seconds." />
        <meta name="keywords" content="product photography, AI photo editor, eBay photos, Etsy photos, Poshmark photos, background remover, reseller tools, online selling, ecommerce photos, Depop, Mercari, Shopify" />
        <link rel="canonical" href="https://pholtr.app/" />
      </Helmet>
      <div className="App-header">
        <div className="hero-container">
          <div className="hero-content">
            <h1>pholtr</h1>
            <h2 className="hero-title">Sell more. Faster.</h2>
            <p className="hero-subtitle">
              Professional product photos in seconds. Don't worry about taking the perfect clutter-free shot — Pholtr handles it.
            </p>
            <div className="social-proof">
              <span className="rating">4.9 ★</span>
              <span className="separator">•</span>
              <span className="users">Loved by resellers</span>
            </div>
            <p className="hero-offer">
              Try 5 photos FREE. No sign up required.
            </p>
            <div className="store-buttons-container">
              <a
                href="https://apps.apple.com/app/pholtr/id6753979622"
                className="store-button"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleAppStoreClick}
              >
                <img
                  src="/app-store-badge.svg"
                  alt="Download on the App Store"
                  className="store-badge"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=nu.hibou.prodshot&pcampaignid=web_share"
                className="store-button"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handlePlayStoreClick}
              >
                <img
                  src="/google-play-badge.svg"
                  alt="Get it on Google Play"
                  className="store-badge"
                />
              </a>
            </div>
          </div>
          <HeroVideo />
        </div>

        <div className="seo-content">
          <section className="content-section">
            <h2>It Starts with a Snap</h2>
            <p>
              Don't worry about taking the perfect clutter-free photo. Just snap a picture of your item, and let Pholtr's AI handle the rest. Get professional, clutter-free product images with great lighting and clean backgrounds.
            </p>
          </section>

          <section className="content-section">
            <h2>The Pholtr Advantage</h2>
            <ul>
              <li><strong>Clean Backgrounds:</strong> Pholtr removes clutter and distractions automatically.</li>
              <li><strong>Automatic Grouping:</strong> Pholtr understands when things belong together.</li>
              <li><strong>Works in Small Rooms:</strong> Challenging perspectives are no match for Pholtr.</li>
              <li><strong>Out-of-the-Box:</strong> Get quality product photos straight from your storage box.</li>
              <li><strong>Content Aware:</strong> Enhanced colors and lighting while keeping details intact.</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Ready to Sell</h2>
            <p>
              Create listings that stand out and sell faster. List your items using product photos that stand out from the rest on eBay, Poshmark, Etsy, Depop, Mercari, Facebook Marketplace, and more.
            </p>
          </section>

          <section className="content-section">
            <h2>How It Works</h2>
            <ol>
              <li>Snap a photo of your item.</li>
              <li>Use Pholtr to enhance your snap in seconds.</li>
              <li>Save and upload directly to your shop.</li>
            </ol>
          </section>

          <section className="content-section">
            <h2>Simple Pricing</h2>
            <p>
              Purchase Credits to transform your snapshots into professional product images. 1 image transformation = 1 Credit. Try 5 photos free — no sign up required.
            </p>
          </section>
        </div>

        <div className="bottom-cta">
          <p className="bottom-cta-text">Ready to sell more?</p>
          <div className="store-buttons-container">
            <a
              href="https://apps.apple.com/app/pholtr/id6753979622"
              className="store-button"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleAppStoreClick}
            >
              <img
                src="/app-store-badge.svg"
                alt="Download on the App Store"
                className="store-badge"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=nu.hibou.prodshot&pcampaignid=web_share"
              className="store-button"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handlePlayStoreClick}
            >
              <img
                src="/google-play-badge.svg"
                alt="Get it on Google Play"
                className="store-badge"
              />
            </a>
          </div>
        </div>

        <footer className="App-footer">
          <a
            href="/terms"
            onClick={() => logEvent('footer_link_click', { link_name: 'terms' })}
          >
            Terms of Service
          </a>
          <span className="footer-separator">•</span>
          <a
            href="/privacy"
            onClick={() => logEvent('footer_link_click', { link_name: 'privacy' })}
          >
            Privacy Policy
          </a>
          <span className="footer-separator">•</span>
          <a
            href="/about"
            onClick={() => logEvent('footer_link_click', { link_name: 'about' })}
          >
            About
          </a>
          <span className="footer-separator">•</span>
          <a
            href="/help"
            onClick={() => logEvent('footer_link_click', { link_name: 'help' })}
          >
            Help
          </a>
        </footer>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/about" element={<About />} />
        <Route path="/help" element={<Help />} />
        <Route path="/r/:code" element={<Referral />} />
        <Route path="/:name" element={<Influencer />} />
      </Routes>
    </Router>
  );
}

export default App;
